import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import Permissions from '../../../auth/Permissions';
import ContactAddressBookSlider from '../../../contacts/ContactAddressBookSlider';
import { getInitialConnection } from '../../../contacts/contactHelpers';
import { CONTACT_ADDRESS_BOOK_SLIDER_ADD_MODE } from '../../../contacts/contactConstants';
import { getLinkId } from '../../formHelpers';
import DropdownFieldSelect from './DropdownFieldSelect';

function FormContactSelector({
  style,
  dropdownStyle,
  layout,
  placeholder,
  numAnswers,
  openLimit,
  onChange,
  value,
  disabled,
  options,
  actualSelected,
  actualSelectedSet,
  projectIdMap,
  dataType,
  settings,
  isExternalForm,
  isFieldDisplay,
  searchByProjectAddress,
  isDisplay,
  sections,
  linkField,
  responses,
  divisions,
}) {
  const [visible, setVisible] = useState(false);
  const canCreateContact = !isExternalForm
    && !isDisplay
    && (Permissions.match('CUSTOMERS') || Permissions.match('VENDORS'));
  const initialConnection = getInitialConnection({ sections, linkField, responses });
  const linkedSelectionId = linkField
    ? getLinkId({ responses, defaultValue: null, linkField })
    : null;

  const openDrawer = useCallback(() => setVisible(true), []);
  const closeDrawer = useCallback(() => setVisible(false), []);

  const onContactCreated = useCallback((entry) => {
    const {
      id: createdId,
      name: createdName,
      customerId: createdCustomerId,
      vendorId: createdVendorId,
    } = entry ?? {};
    if (!createdId) return;

    const selectCreatedContact = () => {
      const createdOption = { label: createdName, value: createdId };
      const isMulti = numAnswers > 1 || openLimit;
      if (isMulti) {
        if (openLimit || value.length < numAnswers) {
          onChange(value.concat(createdId), createdOption);
        }
        return;
      }
      onChange(createdId, createdOption);
    };

    if (linkField) {
      const matchesLinkedSelection = createdCustomerId === linkedSelectionId
        || createdVendorId === linkedSelectionId;
      if (matchesLinkedSelection) selectCreatedContact();
      return;
    }
    selectCreatedContact();
  }, [linkField, linkedSelectionId, numAnswers, onChange, openLimit, value]);

  const dropdownRender = useCallback((menu) => (
    <>
      {menu}
      <div style={{ 
        padding: '8px 8px 4px 8px',
        display: 'flex',
        justifyContent: 'end',
        borderTop: '1px solid #f0f0f0',
      }}>
        <OnTraccrButton
          title="Create New Contact"
          onClick={openDrawer}
          htmlType="button"
        />
      </div>
    </>
  ), [openDrawer]);

  return (
    <>
      <DropdownFieldSelect
        style={style}
        dropdownStyle={dropdownStyle}
        layout={layout}
        placeholder={placeholder}
        numAnswers={numAnswers}
        openLimit={openLimit}
        onChange={onChange}
        value={value}
        disabled={disabled}
        options={options}
        actualSelected={actualSelected}
        actualSelectedSet={actualSelectedSet}
        projectIdMap={projectIdMap}
        dataType={dataType}
        settings={settings}
        isExternalForm={isExternalForm}
        isFieldDisplay={isFieldDisplay}
        searchByProjectAddress={searchByProjectAddress}
        dropdownRender={canCreateContact ? dropdownRender : undefined}
      />
      <ContactAddressBookSlider
        visible={visible}
        mode={CONTACT_ADDRESS_BOOK_SLIDER_ADD_MODE}
        showConnection
        divisions={divisions}
        initialConnection={initialConnection}
        onCreated={onContactCreated}
        onClose={closeDrawer}
      />
    </>
  );
}

FormContactSelector.propTypes = {
  style: PropTypes.shape({}),
  dropdownStyle: PropTypes.string,
  layout: PropTypes.string,
  placeholder: PropTypes.string,
  numAnswers: PropTypes.number,
  openLimit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  actualSelected: PropTypes.arrayOf(PropTypes.shape({})),
  actualSelectedSet: PropTypes.instanceOf(Set),
  projectIdMap: PropTypes.shape({}),
  dataType: PropTypes.string,
  settings: PropTypes.shape({}),
  isExternalForm: PropTypes.bool,
  isFieldDisplay: PropTypes.bool,
  searchByProjectAddress: PropTypes.bool,
  isDisplay: PropTypes.bool,
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  linkField: PropTypes.string,
  responses: PropTypes.shape({}),
  divisions: PropTypes.arrayOf(PropTypes.string),
};

FormContactSelector.defaultProps = {
  style: {},
  dropdownStyle: 'dropdown',
  layout: 'vertical',
  placeholder: '',
  numAnswers: 1,
  openLimit: false,
  value: [],
  disabled: false,
  options: [],
  actualSelected: [],
  actualSelectedSet: new Set(),
  projectIdMap: {},
  dataType: 'Contacts',
  settings: {},
  isExternalForm: false,
  isFieldDisplay: false,
  searchByProjectAddress: false,
  isDisplay: false,
  sections: [],
  linkField: undefined,
  responses: {},
  divisions: [],
};

export default FormContactSelector;
